import React, { useEffect, useState } from 'react';
import { useOAuth } from '@/hooks/useOAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';
import { PlatformLogo } from '@/components/icons/PlatformLogos';
import { cn } from '@/lib/utils';
import { OAuthApiClient } from '@/lib/oauth/OAuthApiClient';
import type { Platform } from '@/lib/oauth/types';

interface AccountReadinessProps {
  className?: string;
}

const platformLabels: Record<Platform, string> = {
  meta: 'Meta',
  google_ads: 'Google',
  tiktok: 'TikTok',
  pinterest: 'Pinterest',
  twitter: 'Twitter',
};

function getConnectionHealth(
  platform: Platform,
  connections: { platform: Platform; is_expired: boolean; token_expires_at?: string }[],
): 'connected' | 'expiring' | 'expired' | 'disconnected' {
  const conn = connections.find(c => c.platform === platform);
  if (!conn) return 'disconnected';
  if (conn.is_expired) return 'expired';
  if (conn.token_expires_at) {
    const expiresAt = new Date(conn.token_expires_at);
    const daysUntilExpiry = (expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    if (daysUntilExpiry < 7) return 'expiring';
  }
  return 'connected';
}

const statusDotColor: Record<string, string> = {
  connected: 'bg-emerald-500',
  expiring: 'bg-amber-500',
  expired: 'bg-bad',
  disconnected: 'bg-gray-300',
};

const oauthClient = new OAuthApiClient();

export const AccountReadiness: React.FC<AccountReadinessProps> = ({ className }) => {
  const { connections } = useOAuth();
  const { subscription } = useSubscription();
  const { currentOrganization } = useAuth();
  const [allPlatforms, setAllPlatforms] = useState<Platform[]>([]);

  useEffect(() => {
    oauthClient
      .getPlatforms()
      .then(setAllPlatforms)
      .catch((error) => {
        console.error('Failed to fetch available platforms:', error);
        setAllPlatforms([]);
      });
  }, []);

  const credits = subscription?.credits;

  // Profile completeness warnings
  const profileWarnings: string[] = [];
  if (!currentOrganization?.website_url) {
    profileWarnings.push('Add a website URL for better ad targeting');
  }
  if (!currentOrganization?.industry) {
    profileWarnings.push('Set your industry for more relevant campaigns');
  }
  if (!currentOrganization?.target_countries || currentOrganization.target_countries.length === 0) {
    profileWarnings.push('Set target countries for geo-targeting');
  }

  // Connection summary
  const connectedCount = connections.filter(c => !c.is_expired).length;
  const hasIssues = connections.some(c => c.is_expired) || profileWarnings.length > 0;

  // Credits info
  const hasCredits = credits && credits.available_credits !== undefined;
  const isUnlimited = credits && (credits.total_credits === -1 || credits.available_credits === -1);
  const creditsLow = credits && !isUnlimited && credits.available_credits < 10;

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-1.5 px-1">
        <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-[11px] font-medium uppercase tracking-wider text-ink-3">
          Account Readiness
        </span>
        {hasIssues && (
          <span className="ml-auto w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
        )}
      </div>

      {/* Platform Connections */}
      <div className="rounded-lg border border-line px-3 py-2.5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-ink-2">Platforms</span>
          <span className="text-[11px] text-ink-3">
            {connectedCount}/{allPlatforms.length} connected
          </span>
        </div>
        <div className="flex items-center gap-2">
          {allPlatforms.map(platform => {
            const health = getConnectionHealth(platform, connections);
            return (
              <div key={platform} className={cn('relative', health === 'disconnected' && 'opacity-40 grayscale')} title={`${platformLabels[platform]}: ${health}`}>
                <PlatformLogo platform={platform} size="sm" />
                <span
                  className={cn(
                    'absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ring-1 ring-white',
                    statusDotColor[health]
                  )}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Credits */}
      {hasCredits && (
        <div className="rounded-lg border border-line px-3 py-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-ink-2">Credits</span>
            <span className={cn(
              'text-[11px]',
              creditsLow ? 'text-amber-500 font-medium' : 'text-ink-3'
            )}>
              {isUnlimited ? 'Unlimited' : `${credits.available_credits} remaining`}
            </span>
          </div>
          {!isUnlimited && (
            <div className="w-full h-1.5 bg-inset rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  credits.percentage_used >= 90
                    ? 'bg-bad'
                    : credits.percentage_used >= 75
                      ? 'bg-amber-500'
                      : 'bg-emerald-500'
                )}
                style={{ width: `${Math.max(2, 100 - credits.percentage_used)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Profile Warnings */}
      {profileWarnings.length > 0 && (
        <div className="rounded-lg border border-warn/30 bg-warn-soft/50 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <svg className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="space-y-1">
              {profileWarnings.map((warning, i) => (
                <p key={i} className="text-[11px] text-warn">{warning}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountReadiness;
